import type { NotificationDismissMode, NotificationSeverity, NotificationSurface } from '@nexus/hub-contracts';
import { getSupabase } from './client';

/** The topics the notifications_signal trigger sends on. supabase/migrations/20261026000004_notifications.sql names the same two. */
const NOTIFICATIONS_EVERYONE_TOPIC = 'notifications:everyone';
const notificationsUserTopic = (userId: string) => `notifications:user:${userId}`;

export type NotificationSignalEvent = 'created' | 'expiry_changed';

/** A nudge, not the notification. The text is read again through PostgREST under RLS. */
export interface NotificationSignal {
  id: string;
  surfaces: string[];
  expiresAt: string | null;
}

export interface FeedNotification {
  id: string;
  severity: NotificationSeverity;
  title: string;
  body?: string;
  action?: { label: string; url: string };
  dismiss: NotificationDismissMode;
  autoDismissMs?: number;
  expiresAt: string | null;
}

// A user holds no grant on created_by, dedup_key or user_id, so `select *` is refused.
const FEED_COLUMNS = [
  'id',
  'severity',
  'title',
  'body',
  'action_label',
  'action_url',
  'dismiss',
  'auto_dismiss_ms',
  'expires_at',
].join(',');

const FEED_LIMIT = 20;

interface FeedRow {
  id: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  action_label: string | null;
  action_url: string | null;
  dismiss: NotificationDismissMode;
  auto_dismiss_ms: number | null;
  expires_at: string | null;
}

interface Answer<T> {
  data: T | null;
  error: { message: string } | null;
}

function toFeedNotification(row: FeedRow): FeedNotification {
  return {
    id: row.id,
    severity: row.severity,
    title: row.title,
    body: row.body ?? undefined,
    action: row.action_label && row.action_url ? { label: row.action_label, url: row.action_url } : undefined,
    dismiss: row.dismiss,
    autoDismissMs: row.auto_dismiss_ms ?? undefined,
    expiresAt: row.expires_at,
  };
}

/**
 * The active notifications for one surface that the user has not dismissed.
 * Row level security leaves out other users' rows and expired rows, and the user's own
 * dismissals are the only ones the join can see, so one request does the whole job.
 */
export async function fetchFeedNotifications(surface: NotificationSurface): Promise<FeedNotification[]> {
  // The tables are not in the generated types yet, as with user_settings.
  const { data, error } = (await getSupabase()
    .from('notifications' as never)
    .select(`${FEED_COLUMNS},notification_dismissals!left(notification_id)`)
    .is('notification_dismissals', null)
    .contains('surfaces', [surface])
    .order('created_at', { ascending: false })
    .limit(FEED_LIMIT)) as Answer<FeedRow[]>;
  if (error) throw new Error(`Notifications request failed: ${error.message}`);

  return (data ?? []).map(toFeedNotification);
}

/** The user id comes from the token, as the column default. Dismissing twice is accepted. */
export async function dismissNotification(id: string): Promise<void> {
  const { error } = (await getSupabase()
    .from('notification_dismissals' as never)
    .upsert({ notification_id: id } as never, {
      onConflict: 'notification_id,user_id',
      ignoreDuplicates: true,
    })) as Answer<unknown>;
  if (error) throw new Error(`Dismissal failed: ${error.message}`);
}

function toSignal(payload: unknown): NotificationSignal | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const { id, surfaces, expires_at: expiresAt } = payload as Record<string, unknown>;
  if (typeof id !== 'string' || !Array.isArray(surfaces)) return null;
  if (expiresAt !== null && typeof expiresAt !== 'string') return null;
  return { id, surfaces: surfaces.filter((surface): surface is string => typeof surface === 'string'), expiresAt };
}

const SIGNAL_EVENTS: NotificationSignalEvent[] = ['created', 'expiry_changed'];

/**
 * Joins the everyone topic and the user's own topic. Both are private: the database policy
 * on realtime.messages lets a user join only those two. `onConnection(true)` means both are joined.
 */
export function subscribeToNotificationSignals({
  userId,
  onSignal,
  onConnection,
}: {
  userId: string;
  onSignal: (event: NotificationSignalEvent, signal: NotificationSignal) => void;
  onConnection: (connected: boolean) => void;
}): () => void {
  const supabase = getSupabase();
  const topics = [NOTIFICATIONS_EVERYONE_TOPIC, notificationsUserTopic(userId)];
  const joined = new Set<string>();
  const channels: ReturnType<typeof supabase.channel>[] = [];
  let stopped = false;

  supabase.realtime
    .setAuth()
    .then(() => {
      if (stopped) return;
      for (const topic of topics) {
        const channel = supabase.channel(topic, { config: { private: true } });
        for (const event of SIGNAL_EVENTS) {
          channel.on('broadcast', { event }, ({ payload }) => {
            const signal = toSignal(payload);
            if (signal) onSignal(event, signal);
          });
        }
        channel.subscribe((status) => {
          if (status === 'SUBSCRIBED') joined.add(topic);
          else joined.delete(topic);
          onConnection(joined.size === topics.length);
        });
        channels.push(channel);
      }
    })
    .catch((err: unknown) => {
      console.warn('[@nexus/supabase] The notification channels were not joined. The feed polls instead.', err);
      onConnection(false);
    });

  return () => {
    stopped = true;
    for (const channel of channels) void supabase.removeChannel(channel);
  };
}
